import { MOSCOW_BUCKETS } from "@/types"
import type { MoscowBucket } from "@/types"

/*
 * The Priorities round's four buckets, from the prototype's QUADS.
 *
 * `label` is MoSCoW because that is the language the room already speaks;
 * `phase` and `when` are the same four buckets read as a plan, which is the only
 * form the leave-behind prints. Keeping both here means the board and the report
 * can never disagree about what "Should have" turns into.
 *
 * The stored keys are `must | should | could | wont` — deliberately without an
 * apostrophe, because they are Firestore map keys. "Won't have" exists only in
 * this file.
 */

export interface BucketCopy {
  label: string
  /** The trade-off that puts a card here, shown under the bucket title. */
  hint: string
  /** What it becomes in the plan. */
  phase: string
  when: string
  /**
   * The last week of that phase, as a number.
   *
   * Beside `when` rather than parsed out of it: the debrief's "2 wks to first
   * value" is the same fact as Must have's "Weeks 1–2", and a regex over the
   * string would break the day someone writes "Weeks 1 to 2".
   */
  weeks: number
}

export const MOSCOW_COPY: Record<MoscowBucket, BucketCopy> = {
  must: {
    label: "Must have",
    hint: "High value · within reach",
    phase: "Phase 1 · Prove",
    when: "Weeks 1–2",
    weeks: 2,
  },
  should: {
    label: "Should have",
    hint: "High value · hard today",
    phase: "Phase 2 · De-risk",
    when: "Weeks 3–4",
    weeks: 4,
  },
  could: {
    label: "Could have",
    hint: "Low value · easy today",
    phase: "Phase 3 · Bank",
    /* Not a committed week, deliberately: a Could have is the bucket the room
       agreed NOT to schedule. Printing "Weeks 5–6" would promise a date nobody
       is holding. */
    when: "Later",
    weeks: 6,
  },
  wont: {
    label: "Won't have",
    hint: "Low value · hard today",
    phase: "Parked",
    when: "—",
    weeks: 0,
  },
}

/**
 * The three buckets the plan is made of. `wont` is a real decision and the
 * report says so, but it is not a phase of work, so it is never in this list.
 */
export const PLAN_BUCKETS: readonly MoscowBucket[] = MOSCOW_BUCKETS.filter(
  (bucket) => bucket !== "wont"
)

export function bucketWeekRange(bucket: MoscowBucket): { from: number; to: number } {
  const position = PLAN_BUCKETS.indexOf(bucket)
  if (position < 0) return { from: 0, to: 0 }
  const from = position === 0 ? 1 : MOSCOW_COPY[PLAN_BUCKETS[position - 1]].weeks + 1
  return { from, to: MOSCOW_COPY[bucket].weeks }
}

/**
 * The buckets the timeline gives a real week range. Could have is excluded on
 * purpose — see its `when` above — so it never draws a numbered column, only
 * the trailing "Later" one.
 */
const COMMITTED_BUCKETS = PLAN_BUCKETS.filter((bucket) => bucket !== "could")

/** The last week a committed bucket reaches — how many numbered columns the
 *  timeline draws before the trailing "Later" column. */
export const TIMELINE_WEEKS = MOSCOW_COPY[COMMITTED_BUCKETS[COMMITTED_BUCKETS.length - 1]].weeks

export const PRIORITIES_COPY = {
  heading: "Set the Priorities",
  objective: "Say why it belongs, then drop it in the quadrant the room agrees on.",
  reasonLabel: "Why does it belong?",
  reasonPlaceholder: "Because this would help us…",
  reasonHint: "Capture the reason first.",
  /*
   * The one label every quadrant carries, empty or not. It is an invitation
   * rather than a status, which is why it does not change: a board whose four
   * corners each report a different state stops reading as a picture.
   */
  dropLabel: "Drop it here",
  allPlacedTitle: "All cards placed",
  allPlacedBody: "Pick a thumbnail below to revisit a card, or read the board back to the room.",
  /** Shown when the facilitator has filed more Must haves than the cap allows. */
  overCap: (cap: number) => `Too many in Must have. The cap is ${cap}.`,
  remaining: (left: number) => `${left} still to place`,
  moveHint: "Drag to move it",
  sendBack: "Send it back to the queue",
} as const
